import {
  CameraOptions,
  MapMouseEvent,
  Shape,
  source as atlasSource} from 'azure-maps-control';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { AuthenticationType, IAzureMapOptions } from 'react-azure-maps';

import { COP_COMMON_ERROR_MESSAGES } from '../../../constants/common-error-messages';
import { COP_API_BASE_URL } from '../../../constants/service-endpoints';
import { get } from '../../../services/http.service';
import { ToastService } from '../../../services/toast.service';
import {
  individualSymbolLayerOptions,
  singleMarkerSymbolLayerOptions
} from '../WorldMap.constants';
import { ExternalMapPoint } from '../WorldMap.types';
import {
  defaultMapControls,
  defaultPosition,
  initialPopupState,
  mapScrollAnimationBase
} from './useWorldMap.constants';
import { InternalMapPoint, PopupState } from './useWorldMap.types';
import {
  averagePosition,
  getShapeAttributes,
  getZoomLevel,
  mapExternalToInternalMapPoints,
  mapPointsToFeatures,
  setupMapLanguage
} from './useWorldMap.utils';

interface UseWorldMapProps {
  autoCenter?: boolean;
  options?: IAzureMapOptions;
  points?: ExternalMapPoint[];
}

export const useWorldMap = ({
  autoCenter,
  options,
  points
}: UseWorldMapProps) => {
  const [popupData, setPopupData] = useState<PopupState>(initialPopupState);
  const [mapPoints, setMapPoints] = useState<InternalMapPoint[]>([]);
  const [mapAuthOptions, setMapAuthOptions] = useState<IAzureMapOptions['authOptions'] | null>(null);

  useEffect(() => {
    const fetchMapConfig = async () => {
      try {
        const url = `${COP_API_BASE_URL}/api/configuration/maps`;
        const data = await get<{ token: string; clientId: string }>(url);

        setMapAuthOptions({
          authType: AuthenticationType.anonymous,
          clientId: data.clientId,
          getToken: (resolve: (value: string) => void) => resolve(data.token)
        });
      } catch (error) {
        const fallbackMessage = COP_COMMON_ERROR_MESSAGES['api-error'].message.replace(
          '{{action}}',
          COP_COMMON_ERROR_MESSAGES['api-error'].action['world-map-config-loading'],
        )
        const message = error instanceof Error && error.message ? error.message : fallbackMessage
        ToastService.error(message);
      }
    };

    fetchMapConfig();
  }, []);

  useEffect(() => {
    if (!points || points.length < 1) {
      return;
    }

    setMapPoints(mapExternalToInternalMapPoints(points));
  }, [points]);

  const activateSingleMapPoint = useCallback((index: number) => {
    setMapPoints((currentPoints) => {
      const nextPoints = [...currentPoints];

      nextPoints.forEach((point) => {
        const { index: pointIndex, isActive: pointIsActive } = point.properties;

        if (pointIsActive && pointIndex !== index) {
          point.properties.isActive = false;
        }
      });

      nextPoints[index].properties.isActive = true;

      return nextPoints;
    });
  }, []);

  const azureMapOptions: IAzureMapOptions = useMemo(() => {
    let center = options?.center ?? defaultPosition;

    if (autoCenter === true) {
      center = averagePosition(mapPoints);
    }

    const authOptions = mapAuthOptions ?? undefined;

    setupMapLanguage('en');

    return {
      ...options,
      authOptions,
      center,
      language: 'en'
    };
  }, [
    options,
    mapPoints,
    autoCenter,
    mapAuthOptions
  ]);

  const { interactive } = azureMapOptions;

  const azureMapControls = useMemo(() => {
    if (interactive) {
      return defaultMapControls;
    }

    return [];
  }, [interactive]);

  const dataSourceCollection = useMemo(() => {
    if (
      mapPoints === null ||
      mapPoints === undefined ||
      mapPoints.length === 0
    ) {
      return [];
    }

    return mapPointsToFeatures(mapPoints);
  }, [mapPoints]);

  const onBubbleClick = useCallback(
    (e: MapMouseEvent) => {
      if (!e.shapes || e.shapes.length <= 0 || interactive === false) {
        return;
      }

      const { cluster, point } = getShapeAttributes(e.shapes[0] as Shape);

      if (cluster !== undefined) {
        const { coordinates, properties, source } = cluster;

        const dataSource = e.map.sources.getById(
          source
        ) as atlasSource.DataSource;

        const currentZoom = e.map.getCamera().zoom || 0;

        dataSource
          .getClusterExpansionZoom(properties.cluster_id)
          .then((zoom: number) => {
            const minZoomToSplit = 19;
            const targetZoom = Math.max(zoom + 4, currentZoom + 6, minZoomToSplit);
            e.map.setCamera({
              ...mapScrollAnimationBase,
              center: coordinates,
              zoom: targetZoom
            });
          });
        return;
      }

      if (point !== undefined) {
        const { coordinates, customProperties } = point;

        if (!coordinates) {
          return;
        }

        activateSingleMapPoint(customProperties.index);

        e.map.setCamera({
          ...mapScrollAnimationBase,
          center: coordinates
        });

        setPopupData({
          isVisible: true,
          position: coordinates,
          properties: customProperties
        });
      }
    },
    [interactive, activateSingleMapPoint]
  );

  const cameraOptions = useMemo(() => {

    if (!autoCenter && options?.center && options?.zoom !== undefined) {
      return {
        center: options.center,
        duration: 200,
        type: 'ease',
        zoom: options.zoom
      } as CameraOptions;
    }

    const points = mapPoints.some((point) => point.properties.isActive)
      ? mapPoints.filter((point) => point.properties.isActive)
      : mapPoints;

    return {
      center: averagePosition(points),
      duration: 200,
      type: 'ease',
      zoom: getZoomLevel(points)
    } as CameraOptions;
  }, [autoCenter, options?.center, options?.zoom, mapPoints]);

  const onPointClick = useCallback(
    (e: MapMouseEvent) => {
      if (!e.shapes || e.shapes.length <= 0 || interactive === false) {
        return;
      }

      const { point } = getShapeAttributes(e.shapes[0] as Shape);

      if (point === undefined) {
        return;
      }

      const { coordinates, customProperties } = point;

      activateSingleMapPoint(customProperties.index);

      e.map.setCamera({
        ...mapScrollAnimationBase,
        center: coordinates
      });


      setPopupData({
        isVisible: true,
        position: coordinates,
        properties: customProperties
      });
    },
    [interactive, activateSingleMapPoint]
  );

  const closePopup = useCallback(() => {
    setPopupData(initialPopupState);
  }, []);

  const bubblesLayerEvents = useMemo(
    () => ({ click: onBubbleClick }),
    [onBubbleClick]
  );

  const pinsLayerEvents = useMemo(
    () => ({ click: onPointClick }),
    [onPointClick]
  );

  const pinMarkerSymbolLayerOptions = useMemo(
    () => (mapPoints.length > 1 ? singleMarkerSymbolLayerOptions : individualSymbolLayerOptions),
    [mapPoints.length]
  );

  return {
    azureMapControls,
    azureMapOptions,
    bubblesLayerEvents,
    cameraOptions,
    closePopup,
    dataSourceCollection,
    isAuthReady: mapAuthOptions !== null,
    pinMarkerSymbolLayerOptions,
    pinsLayerEvents,
    popupData
  };
};

